import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { User, Loader2 } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';
import RichTextEditor from '@/components/ui/RichTextEditor';

interface SchoolMain {
    slate_id: number;
    slate_name: string;
    slate_slug: string;
    city: number;
    area_id_fk: number;
    lat: string;
    lng: string;
    verified: number;
    claimed: number;
    is_client: number;
    featured: number;
    is_spotlight: number;
    views: number;
    admissions_open: number;
    city_name?: string;
    area_name?: string;
}

interface SchoolInfo {
    info_id: number;
    email: string;
    phone: string;
    phone2?: string;
    landline?: string;
    website?: string;
    directions?: string;
    address?: string;
    location_notes?: string;
    board_id?: number;
    board1_id?: number;
    board2_id?: number;
    collection_id?: number;
    collection1_id?: number;
    established?: string;
    grade_from?: string;
    grade_to?: string;
    description?: string;
    medium?: string;
    medium1?: string;
    medium2?: string;
    school_type?: string;
    school_type_text?: string;
    primary_language?: string;
    secondary_language?: string;
}

interface SchoolFacility {
    facility_id: number;
    total_enrollment?: string;
    total_boys_student?: number;
    total_girls_student?: number;
    management?: string;
    total_students?: number;
    total_teachers?: number;
    campus_acreage?: string;
    provides_food?: number;
}

interface Board {
    board_id: number;
    board_name: string;
    board_slug: string;
}

interface Collection {
    collection_id: number;
    collection_name: string;
    collection_slug: string;
}

interface City {
    cityId: number;
    cityName: string;
    citySlug: string;
    country_id?: number;
}

interface Area {
    areaId: number;
    areaName: string;
    areaSlug: string;
    city_id_fk: number;
}

interface BasicInfoData {
    school_main: SchoolMain;
    school_info: SchoolInfo | null;
    school_facility: SchoolFacility | null;
    dropdowns: {
        boards: Board[];
        collections: Collection[];
        cities: City[];
        areas: Area[];
    };
}


// Static dropdown options - value is what's stored in DB, label is what's displayed
const GRADE_OPTIONS = [
   // { value: "Pre-Nursery", label: "Pre-Nursery" },
    { value: "Nursery", label: "Nursery" },
    { value: "LKG", label: "LKG" },
    { value: "UKG", label: "UKG" },
    { value: "1", label: "Grade 1" },
    { value: "2", label: "Grade 2" },
    { value: "3", label: "Grade 3" },
    { value: "4", label: "Grade 4" },
    { value: "5", label: "Grade 5" },
    { value: "6", label: "Grade 6" },
    { value: "7", label: "Grade 7" },
    { value: "8", label: "Grade 8" },
    { value: "9", label: "Grade 9" },
    { value: "10", label: "Grade 10" },
    { value: "11", label: "Grade 11" },
    { value: "12", label: "Grade 12" }
];

const MEDIUM_OPTIONS = [
    "English",
    "Gujarati",
    "Hindi",
    "Kannada",
    "Marathi",
    "Telugu",
    "Urdu",
    "Tamil",
    "Bengali"
];

const LANGUAGE_OPTIONS = [
    "English",
    "Hindi",
    "Telugu",
    "Tamil",
    "Kannada",
    "Malayalam",
    "Marathi",
    "Gujarati",
    "Bengali",
    "Punjabi",
    "Urdu",
    "Sanskrit",
    "Spanish",
    "French",
    "German",
    "Arabic",
    "Mandarin",
    "Japanese",
    "Other"
];

const SCHOOL_TYPE_OPTIONS = [
    { value: "1", label: "Boys" },
    { value: "2", label: "Girls" },
    { value: "3", label: "Co-Education" }
];

const MANAGEMENT_TYPE_OPTIONS = [
    { value: "1", label: "Private" },
    { value: "2", label: "Government Aided" },
    { value: "3", label: "Unaided" },
    { value: "4", label: "Private Aided" },
    { value: "5", label: "Un-Recognised" },
    { value: "6", label: "Partially Aided" },
    { value: "7", label: "Others" }
];

const BasicInfo: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState<BasicInfoData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedMediums, setSelectedMediums] = useState<string[]>([]);
    const [allCities, setAllCities] = useState<City[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<number>(1);

    // TODO: Get client_id from user context or school data
    // For now, using a placeholder - replace with actual client_id from context/API
  const userStr = localStorage.getItem('crm_user');
  const user = userStr ? JSON.parse(userStr) : null;
  const clientId = user?.data?.data?.client_id_fk || user?.data?.client_id_fk || user?.client_id_fk || null;

    useEffect(() => {
        fetchBasicInfo();
    }, [id]);

    useEffect(() => {
        if (data?.dropdowns?.cities && data.dropdowns.cities.length > 0) {
            setAllCities(data.dropdowns.cities);
            const currentCity = data.dropdowns.cities.find(c => c.cityId === data.school_main.city);
            const countryId = currentCity?.country_id ?? 1;
            setSelectedCountry(countryId);
        }
    }, [data?.school_main?.slate_id]);

    useEffect(() => {
        if (allCities.length === 0) return;
        const filtered = allCities.filter(c => c.country_id === selectedCountry);
        setData(prev => {
            if (!prev) return prev;
            return { ...prev, dropdowns: { ...prev.dropdowns, cities: filtered } };
        });
    }, [selectedCountry, allCities]);

    useEffect(() => {
        // Initialize selected mediums from data
        if (data?.school_info) {
            const mediums: string[] = [];
            if (data.school_info.medium) mediums.push(data.school_info.medium);
            if (data.school_info.medium1) mediums.push(data.school_info.medium1);
            if (data.school_info.medium2) mediums.push(data.school_info.medium2);
            setSelectedMediums(mediums);
        }
    }, [data?.school_info?.medium, data?.school_info?.medium1, data?.school_info?.medium2]);

    // Helper function to handle medium selection (max 3)
    const handleMediumChange = (medium: string) => {
        setSelectedMediums(prev => {
            if (prev.includes(medium)) {
                // Remove if already selected
                const updated = prev.filter(m => m !== medium);
                updateMediumsInData(updated);
                return updated;
            } else {
                // Add if less than 3 selected
                if (prev.length < 3) {
                    const updated = [...prev, medium];
                    updateMediumsInData(updated);
                    return updated;
                } else {
                    showToast('Maximum 3 mediums can be selected', 'error');
                    return prev;
                }
            }
        });
    };

    // Update medium, medium1, medium2 in data
    const updateMediumsInData = (mediums: string[]) => {
        if (data?.school_info) {
            setData({
                ...data,
                school_info: {
                    ...data.school_info,
                    medium: mediums[0] || '',
                    medium1: mediums[1] || '',
                    medium2: mediums[2] || ''
                }
            });
        }
    };

    // Helper function to handle school type change
    const handleSchoolTypeChange = (value: string) => {
        if (data?.school_info) {
            const selectedOption = SCHOOL_TYPE_OPTIONS.find(opt => opt.value === value);
            setData({
                ...data,
                school_info: {
                    ...data.school_info,
                    school_type: value,
                    school_type_text: selectedOption?.label || ''
                }
            });
        }
    };

    const fetchBasicInfo = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiRequest(`/schools/${id}/basic-info`);
            console.log('=== Basic Info API Response ===');
            console.log('Full Response:', response);
            console.log('Areas Data:', response.data?.dropdowns?.areas);
            console.log('Total Areas:', response.data?.dropdowns?.areas?.length);
            console.log('Current City:', response.data?.school_main?.city);

            if (response.success && response.data) {
                setData(response.data);

                // Save school name to localStorage for sidebar
                if (response.data.school_main && response.data.school_main.slate_name) {
                    localStorage.setItem(`school_name_${id}`, response.data.school_main.slate_name);
                    // Dispatch custom event to notify sidebar
                    window.dispatchEvent(new CustomEvent('schoolNameUpdated', {
                        detail: { id, name: response.data.school_main.slate_name }
                    }));
                }
            } else {
                setError(response.message || 'Failed to load school information');
            }
        } catch (err: any) {
            console.error('Error fetching basic info:', err);
            setError('Failed to load school information');
        } finally {
            setLoading(false);
        }
    };

    const fetchAreasByCity = async (cityId: number) => {
        try {
            console.log('=== Fetching areas for city:', cityId, '===');
            const response = await apiRequest(`/schools/areas/${cityId}`);
            console.log('Areas API Response:', response);

            if (response.success && response.data && response.data.areas) {
                console.log('Fetched', response.data.areas.length, 'areas for city', cityId);
                // Update areas in dropdowns using functional update to avoid stale state
                setData(prevData => {
                    if (!prevData) return prevData;
                    return {
                        ...prevData,
                        dropdowns: {
                            ...prevData.dropdowns,
                            areas: response.data.areas
                        }
                    };
                });
            } else {
                console.error('Failed to fetch areas:', response.message);
                // Reset areas to empty array on failure
                setData(prevData => {
                    if (!prevData) return prevData;
                    return {
                        ...prevData,
                        dropdowns: {
                            ...prevData.dropdowns,
                            areas: []
                        }
                    };
                });
            }
        } catch (err: any) {
            console.error('Error fetching areas:', err);
            // Reset areas to empty array on error
            setData(prevData => {
                if (!prevData) return prevData;
                return {
                    ...prevData,
                    dropdowns: {
                        ...prevData.dropdowns,
                        areas: []
                    }
                };
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!data) return;

        try {
            setSaving(true);
            setError(null);

            const payload = {
                school_main: {
                    slate_name: data.school_main.slate_name,
                    city: data.school_main.city,
                    area_id_fk: data.school_main.area_id_fk,
                    lat: data.school_main.lat,
                    lng: data.school_main.lng,
                    verified: data.school_main.verified,
                    claimed: data.school_main.claimed,
                    is_client: data.school_main.is_client,
                    is_spotlight: data.school_main.is_spotlight,
                    views: data.school_main.views,
                },
                school_info: data.school_info ? {
                    email: data.school_info.email,
                    phone: data.school_info.phone,
                    phone2: data.school_info.phone2,
                    landline: data.school_info.landline,
                    website: data.school_info.website,
                    directions: data.school_info.directions,
                    address: data.school_info.address,
                    location_notes: data.school_info.location_notes,
                    board_id: data.school_info.board_id,
                    board1_id: data.school_info.board1_id,
                    board2_id: data.school_info.board2_id,
                    collection_id: data.school_info.collection_id,
                    collection1_id: data.school_info.collection1_id,
                    established: data.school_info.established,
                    grade_from: data.school_info.grade_from,
                    grade_to: data.school_info.grade_to,
                    description: data.school_info.description,
                    medium: data.school_info.medium,
                    medium1: data.school_info.medium1,
                    medium2: data.school_info.medium2,
                    school_type: data.school_info.school_type,
                    school_type_text: data.school_info.school_type_text,
                    primary_language: data.school_info.primary_language,
                    secondary_language: data.school_info.secondary_language,
                } : {},
                school_facility: data.school_facility ? {
                    total_enrollment: data.school_facility.total_enrollment,
                    total_boys_student: data.school_facility.total_boys_student,
                    total_girls_student: data.school_facility.total_girls_student,
                    management: data.school_facility.management,
                    total_students: data.school_facility.total_students,
                    total_teachers: data.school_facility.total_teachers,
                    campus_acreage: data.school_facility.campus_acreage,
                    provides_food: data.school_facility.provides_food,
                } : {},
            };

            const response = await apiRequest(`/schools/${id}/basic-info`, {
                method: 'PUT',
                body: payload,
            });

            if (response.success) {
                showToast('School information updated successfully!', 'success');
                window.dispatchEvent(new CustomEvent('schoolIsClientUpdated', {
                    detail: { id, is_client: data.school_main?.is_client }
                }));
            } else {
                throw new Error(response.message || 'Failed to update');
            }
        } catch (err: any) {
            console.error('Error saving basic info:', err);
            const errorMessage = err.message || 'Failed to save school information';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="p-8">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-8">
                <p className="text-gray-500">No data available</p>
            </div>
        );
    }

    return (
        <div>
            {/* Header - Fixed */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <User className="w-6 h-6" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h1>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Manage school basic details and contact information</p>
                        </div>
                    </div>
                    <div className="flex-shrink-0">
                        <button
                            type="submit"
                            form="basic-info-form"
                            disabled={saving}
                            className="inline-flex items-center px-4 py-2 bg-brandColor hover:bg-brand-hover text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mx-8 mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            {/* Form */}
            <form id="basic-info-form" onSubmit={handleSubmit} className="p-8 space-y-6">
                {/* School Main Information */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">School Details</h2>
                    </div>
                    <div className="p-6">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                School Name *
                            </label>
                            <input
                                type="text"
                                value={data.school_main.slate_name}
                                onChange={(e) => setData({
                                    ...data,
                                    school_main: { ...data.school_main, slate_name: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                School ID
                            </label>
                            <input
                                type="text"
                                value={data.school_main.slate_id}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                City *
                            </label>
                            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden mb-2">
                                {[{ label: '🇮🇳 India', value: 1 }, { label: '🇦🇪 UAE', value: 2 }].map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => {
                                            setSelectedCountry(opt.value);
                                            setData(prev => prev ? { ...prev, school_main: { ...prev.school_main, city: 0, area_id_fk: 0 } } : prev);
                                        }}
                                        className={`flex-1 py-1.5 text-sm font-medium transition-colors ${selectedCountry === opt.value ? 'bg-brandColor text-white' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <select
                                value={data.school_main.city || ''}
                                onChange={(e) => {
                                    const newCityId = Number(e.target.value);
                                    console.log('=== City Changed ===');
                                    console.log('Old City:', data.school_main.city);
                                    console.log('New City:', newCityId);
                                    console.log('Old Area:', data.school_main.area_id_fk);
                                    console.log('Resetting area to 0 and fetching new areas');
                                    setData({
                                        ...data,
                                        school_main: {
                                            ...data.school_main,
                                            city: newCityId,
                                            area_id_fk: 0 // Reset area when city changes
                                        }
                                    });
                                    // Fetch areas for the new city
                                    if (newCityId) {
                                        fetchAreasByCity(newCityId);
                                    }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                required
                            >
                                <option value="">Select City</option>
                                {data.dropdowns.cities.map((city) => (
                                    <option key={city.cityId} value={city.cityId}>
                                        {city.cityName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Area
                            </label>
                            <select
                                value={data.school_main.area_id_fk || ''}
                                onChange={(e) => setData({
                                    ...data,
                                    school_main: { ...data.school_main, area_id_fk: Number(e.target.value) }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                            >
                                <option value="">Select Area</option>
                                {data.dropdowns.areas.map((area) => (
                                    <option key={area.areaId} value={area.areaId}>
                                        {area.areaName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Latitude
                            </label>
                            <input
                                type="text"
                                value={data.school_main.lat || ''}
                                onChange={(e) => setData({
                                    ...data,
                                    school_main: { ...data.school_main, lat: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Longitude
                            </label>
                            <input
                                type="text"
                                value={data.school_main.lng || ''}
                                onChange={(e) => setData({
                                    ...data,
                                    school_main: { ...data.school_main, lng: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                            />
                        </div>
                         {clientId === 9 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Views
                            </label>
                            <input
                                type="number"
                                value={data.school_main.views || 0}
                                onChange={(e) => setData({
                                    ...data,
                                    school_main: { ...data.school_main, views: Number(e.target.value) }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                            />
                        </div>
                           )}
                        {/* Show Verified, Claimed, and Is Client checkboxes only for client_id = 9 */}
                        {clientId === 9 && (
                            <div className="flex items-center space-x-6">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={data.school_main.verified === 1}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_main: { ...data.school_main, verified: e.target.checked ? 1 : 0 }
                                        })}
                                        className="mr-2 h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 dark:border-gray-600 rounded"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Verified</span>
                                </label>

                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={data.school_main.claimed === 1}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_main: { ...data.school_main, claimed: e.target.checked ? 1 : 0 }
                                        })}
                                        className="mr-2 h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 dark:border-gray-600 rounded"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Claimed</span>
                                </label>

                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={data.school_main.is_client === 1}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_main: { ...data.school_main, is_client: e.target.checked ? 1 : 0 }
                                        })}
                                        className="mr-2 h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 dark:border-gray-600 rounded"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Is Client</span>
                                </label>

                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={data.school_main.is_spotlight === 1}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_main: { ...data.school_main, is_spotlight: e.target.checked ? 1 : 0 }
                                        })}
                                        className="mr-2 h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 dark:border-gray-600 rounded"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Spotlight School</span>
                                </label>
                            </div>
                        )}
                    </div>
                    </div>
                </div>

                {/* Contact Information */}
                {data.school_info && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-brandColor px-6 py-3">
                            <h2 className="text-lg font-semibold text-white">Contact Information</h2>
                        </div>
                        <div className="p-6">

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={data.school_info.email || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_info: data.school_info ? { ...data.school_info, email: e.target.value } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Phone
                                </label>
                                <input
                                    type="text"
                                    value={data.school_info.phone || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_info: data.school_info ? { ...data.school_info, phone: e.target.value } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Website
                                </label>
                                <input
                                    type="url"
                                    value={data.school_info.website || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_info: data.school_info ? { ...data.school_info, website: e.target.value } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Established Year
                                </label>
                                <input
                                    type="text"
                                    value={data.school_info.established || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_info: data.school_info ? { ...data.school_info, established: e.target.value } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Google Maps URL
                                </label>
                                <input
                                    type="url"
                                    value={data.school_info.directions || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_info: data.school_info ? { ...data.school_info, directions: e.target.value } : null
                                    })}
                                    placeholder="https://maps.google.com/..."
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Address
                                </label>
                                <textarea
                                    value={data.school_info.address || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_info: data.school_info ? { ...data.school_info, address: e.target.value } : null
                                    })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Location Notes
                                </label>
                                <textarea
                                    value={data.school_info.location_notes || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_info: data.school_info ? { ...data.school_info, location_notes: e.target.value } : null
                                    })}
                                    rows={2}
                                    placeholder="e.g., Near XYZ Mall, Opposite ABC Park, Next to PQR Hospital"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                />
                            </div>
                        </div>
                        </div>
                    </div>
                )}

                {/* School Classification */}
                {data.school_info && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-brandColor px-6 py-3">
                            <h2 className="text-lg font-semibold text-white">School Classification</h2>
                        </div>
                        <div className="p-6">

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    School Type *
                                </label>
                                <select
                                    value={data.school_info.school_type || ''}
                                    onChange={(e) => handleSchoolTypeChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    required
                                >
                                    <option value="">Select School Type</option>
                                    {SCHOOL_TYPE_OPTIONS.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Medium of Instruction * (Select up to 3)
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {MEDIUM_OPTIONS.map((medium) => (
                                        <label key={medium} className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedMediums.includes(medium)}
                                                onChange={() => handleMediumChange(medium)}
                                                className="mr-2 h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 dark:border-gray-600 rounded"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{medium}</span>
                                        </label>
                                    ))}
                                </div>
                                {selectedMediums.length > 0 && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                        Selected: {selectedMediums.join(', ')} ({selectedMediums.length}/3)
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Primary Language
                                    </label>
                                    <select
                                        value={data.school_info.primary_language || ''}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_info: data.school_info ? { ...data.school_info, primary_language: e.target.value } : null
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    >
                                        <option value="">Select Primary Language</option>
                                        {LANGUAGE_OPTIONS.map((lang) => (
                                            <option key={lang} value={lang}>{lang}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Secondary Language
                                    </label>
                                    <select
                                        value={data.school_info.secondary_language || ''}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_info: data.school_info ? { ...data.school_info, secondary_language: e.target.value } : null
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    >
                                        <option value="">Select Secondary Language</option>
                                        {LANGUAGE_OPTIONS.map((lang) => (
                                            <option key={lang} value={lang}>{lang}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>
                )}

                {/* Academic Information */}
                {data.school_info && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-brandColor px-6 py-3">
                            <h2 className="text-lg font-semibold text-white">Academic Information</h2>
                        </div>
                        <div className="p-6">

                        <div className="space-y-4">
                            {/* Boards - All in one row */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Primary Board/Curriculum *
                                    </label>
                                    <select
                                        value={data.school_info.board_id || ''}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_info: data.school_info ? { ...data.school_info, board_id: Number(e.target.value) } : null
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        required
                                    >
                                        <option value="">Select Board</option>
                                        {data.dropdowns.boards.map((board) => (
                                            <option key={board.board_id} value={board.board_id}>
                                                {board.board_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Secondary Board/Curriculum
                                    </label>
                                    <select
                                        value={data.school_info.board1_id || ''}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_info: data.school_info ? { ...data.school_info, board1_id: Number(e.target.value) } : null
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    >
                                        <option value="">Select Board</option>
                                        {data.dropdowns.boards.map((board) => (
                                            <option key={board.board_id} value={board.board_id}>
                                                {board.board_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Tertiary Board/Curriculum
                                    </label>
                                    <select
                                        value={data.school_info.board2_id || ''}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_info: data.school_info ? { ...data.school_info, board2_id: Number(e.target.value) } : null
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    >
                                        <option value="">Select Board</option>
                                        {data.dropdowns.boards.map((board) => (
                                            <option key={board.board_id} value={board.board_id}>
                                                {board.board_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Categories - Both in one row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Primary Category
                                    </label>
                                    <select
                                        value={data.school_info.collection_id || ''}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_info: data.school_info ? { ...data.school_info, collection_id: Number(e.target.value) } : null
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    >
                                        <option value="">Select Category</option>
                                        {data.dropdowns.collections.map((collection) => (
                                            <option key={collection.collection_id} value={collection.collection_id}>
                                                {collection.collection_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Secondary Category
                                    </label>
                                    <select
                                        value={data.school_info.collection1_id || ''}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_info: data.school_info ? { ...data.school_info, collection1_id: Number(e.target.value) } : null
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    >
                                        <option value="">Select Category</option>
                                        {data.dropdowns.collections.map((collection) => (
                                            <option key={collection.collection_id} value={collection.collection_id}>
                                                {collection.collection_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Grade Range - Both in one row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Grade From *
                                    </label>
                                    <select
                                        value={data.school_info.grade_from || ''}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_info: data.school_info ? { ...data.school_info, grade_from: e.target.value } : null
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        required
                                    >
                                        <option value="">Select Grade</option>
                                        {GRADE_OPTIONS.map((grade) => (
                                            <option key={grade.value} value={grade.value}>
                                                {grade.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Grade To *
                                    </label>
                                    <select
                                        value={data.school_info.grade_to || ''}
                                        onChange={(e) => setData({
                                            ...data,
                                            school_info: data.school_info ? { ...data.school_info, grade_to: e.target.value } : null
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        required
                                    >
                                        <option value="">Select Grade</option>
                                        {GRADE_OPTIONS.map((grade) => (
                                            <option key={grade.value} value={grade.value}>
                                                {grade.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>
                )}

                {/* Enrollment & Management */}
                {data.school_facility && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-brandColor px-6 py-3">
                            <h2 className="text-lg font-semibold text-white">Enrollment & Management</h2>
                        </div>
                        <div className="p-6">

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Management Type
                                </label>
                                <select
                                    value={data.school_facility.management || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_facility: data.school_facility ? { ...data.school_facility, management: e.target.value } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                >
                                    <option value="">Select Management Type</option>
                                    {MANAGEMENT_TYPE_OPTIONS.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Total Enrollment
                                </label>
                                <input
                                    type="text"
                                    value={data.school_facility.total_enrollment || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_facility: data.school_facility ? { ...data.school_facility, total_enrollment: e.target.value } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    placeholder="Total number of students"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Total Boys Students
                                </label>
                                <input
                                    type="number"
                                    value={data.school_facility.total_boys_student || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_facility: data.school_facility ? { ...data.school_facility, total_boys_student: Number(e.target.value) } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    placeholder="Number of boys"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Total Girls Students
                                </label>
                                <input
                                    type="number"
                                    value={data.school_facility.total_girls_student || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_facility: data.school_facility ? { ...data.school_facility, total_girls_student: Number(e.target.value) } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    placeholder="Number of girls"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Total Students (for ratio)
                                </label>
                                <input
                                    type="number"
                                    value={data.school_facility.total_students || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_facility: data.school_facility ? { ...data.school_facility, total_students: Number(e.target.value) } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    placeholder="Total students for teacher ratio"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Total Teachers (for ratio)
                                </label>
                                <input
                                    type="number"
                                    value={data.school_facility.total_teachers || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_facility: data.school_facility ? { ...data.school_facility, total_teachers: Number(e.target.value) } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    placeholder="Total teachers for student ratio"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Campus Acreage (acres)
                                </label>
                                <input
                                    type="text"
                                    value={data.school_facility.campus_acreage || ''}
                                    onChange={(e) => setData({
                                        ...data,
                                        school_facility: data.school_facility ? { ...data.school_facility, campus_acreage: e.target.value } : null
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    placeholder="e.g. 20"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Does the school provide food?
                                </label>
                                <div className="flex items-center gap-6">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="provides_food"
                                            value="1"
                                            checked={data.school_facility.provides_food === 1}
                                            onChange={() => setData({
                                                ...data,
                                                school_facility: data.school_facility ? { ...data.school_facility, provides_food: 1 } : null
                                            })}
                                            className="mr-2 h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">Yes</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="provides_food"
                                            value="0"
                                            checked={data.school_facility.provides_food === 0}
                                            onChange={() => setData({
                                                ...data,
                                                school_facility: data.school_facility ? { ...data.school_facility, provides_food: 0 } : null
                                            })}
                                            className="mr-2 h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">No</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>
                )}

                {/* About School */}
                {data.school_info && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-brandColor px-6 py-3">
                            <h2 className="text-lg font-semibold text-white">About School</h2>
                        </div>
                        <div className="p-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    School Description
                                </label>
                                <RichTextEditor
                                    value={data.school_info.description || ''}
                                    onChange={(value) => setData({
                                        ...data,
                                        school_info: data.school_info ? { ...data.school_info, description: value } : null
                                    })}
                                    placeholder="Write about the school..."
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                    Use the toolbar above to format your text with bold, italic, lists, and more.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default BasicInfo;
